import * as React from "react";
import Link from "next/link";
import { ChevronDown, Search, SlidersHorizontal } from "lucide-react";

import type { FunctionalAreaFacet, LocationFacet, PublicJob, PublicJobSearch } from "../../lib/job-board/types";
import { groupFunctionalAreaFacets } from "../../lib/job-board/functional-area-utils";
import { jobUrl } from "../../lib/job-board/url";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type Filters = { q: string; location: string; functionalArea: string; secondaryFunctionalArea: string; page: number };

type Props = {
  initialJobs: PublicJobSearch;
  initialLocations: LocationFacet[];
  initialFunctionalAreas: FunctionalAreaFacet[];
  initialFilters: Filters;
  initialError?: boolean;
};

function queryString(filters: Filters) {
  const query = new URLSearchParams();
  if (filters.q) query.set("q", filters.q);
  if (filters.location) query.set("location", filters.location);
  if (filters.functionalArea) query.set("functionalArea", filters.functionalArea);
  if (filters.secondaryFunctionalArea) query.set("secondaryFunctionalArea", filters.secondaryFunctionalArea);
  query.set("page", String(filters.page));
  query.set("limit", "15");
  return query.toString();
}

function selectedFunctionalAreas(value: string): Set<string> {
  return new Set(value.split(",").map((area) => area.trim()).filter(Boolean));
}

function functionalAreaSelection(filters: Filters, facets: FunctionalAreaFacet[]) {
  const categories = groupFunctionalAreaFacets(facets);
  const legacyValues = selectedFunctionalAreas(filters.functionalArea);
  const primaryValues = new Set<string>();
  const secondaryValues = selectedFunctionalAreas(filters.secondaryFunctionalArea);
  const specializations = new Set<string>();

  for (const category of categories) {
    for (const specialization of category.specializations) specializations.add(specialization.value);
    if (legacyValues.has(category.value)) {
      primaryValues.add(category.value);
      for (const specialization of category.specializations) {
        if (legacyValues.has(specialization.value)) secondaryValues.add(specialization.value);
      }
    }
  }
  // Preserve unknown legacy primary values rather than dropping a compatible URL.
  for (const value of legacyValues) if (!specializations.has(value)) primaryValues.add(value);

  return { primaryValues, secondaryValues };
}

function employmentLabel(jobType: string): string {
  return jobType.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function experienceLabel(job: PublicJob): string | null {
  const { min, max } = job.experience;
  if (min === undefined && max === undefined) return null;
  if (min !== undefined && max !== undefined) return `${min}–${max} years`;
  return min !== undefined ? `${min}+ years` : `Up to ${max} years`;
}

function postedLabel(date: string | null): string {
  if (!date) return "Recently posted";
  const time = new Date(date).getTime();
  if (!Number.isFinite(time)) return "Recently posted";
  const days = Math.max(0, Math.floor((Date.now() - time) / 86_400_000));
  return days === 0 ? "Today" : `${days}d ago`;
}

function isNew(date: string | null): boolean {
  if (!date) return false;
  const time = new Date(date).getTime();
  return Number.isFinite(time) && Date.now() - time <= 7 * 86_400_000;
}

function JobResultCard({ job }: { job: PublicJob }) {
  const experience = experienceLabel(job);
  const metadata = [job.location || "Location not specified", job.jobType ? employmentLabel(job.jobType) : null, experience].filter(Boolean).join(" · ");
  const avatarInitial = job.title.trim().charAt(0).toUpperCase() || "J";
  const functionalArea = job.functionalArea?.secondary || job.functionalArea?.primary;
  const description = job.description.replace(/<[^>]*>/g, "").trim();

  return (
    <Card className="job-card mb-3 overflow-hidden border-border shadow-sm transition-shadow hover:shadow-md">
      <Link href={jobUrl(job.title, job.id)} className="flex items-center gap-4 p-5 no-underline outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" aria-label={`View job: ${job.title}`}>
        <Avatar className="h-11 w-11 rounded-md bg-secondary text-primary">
          <AvatarFallback className="rounded-md bg-secondary font-heading font-semibold text-primary">{avatarInitial}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <h2 className="m-0 text-job-title hover:text-primary">{job.title}</h2>
          <p className="mb-0 mt-1 text-job-meta">{metadata}</p>
          {functionalArea ? <p className="mb-0 mt-3 text-sm text-muted-foreground"><span className="font-medium text-foreground">Key area:</span> {functionalArea}</p> : null}
          {description ? <p className="mb-0 mt-1 line-clamp-2 text-sm leading-6 text-muted-foreground">{description}</p> : null}
          {(job.isFeatured || isNew(job.createdAt)) ? <div className="mt-2 flex flex-wrap gap-1.5">{job.isFeatured ? <Badge variant="featured">Featured</Badge> : null}{isNew(job.createdAt) ? <Badge variant="new">New</Badge> : null}</div> : null}
        </div>
        <div className="hidden shrink-0 text-right sm:block">
          <p className="m-0 text-job-meta">{postedLabel(job.createdAt)}</p>
        </div>
      </Link>
    </Card>
  );
}

function FunctionalAreaFilters({ facets, selected, disabled, onToggle }: {
  facets: FunctionalAreaFacet[];
  selected: Set<string>;
  disabled: boolean;
  onToggle: (primary: string, secondary: string) => void;
}) {
  const categories = React.useMemo(() => groupFunctionalAreaFacets(facets), [facets]);

  return (
    <aside className="filter-wrapper" aria-label="Filter jobs by functional area">
      <div>
        <div className="mb-3 flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-primary" aria-hidden="true" />
          <p className="text-eyebrow m-0">Functional area</p>
        </div>
        {categories.length === 0 ? <p className="m-0 text-xs text-muted-foreground">No functional-area filters available.</p> : null}
        <div className="border-t border-border">
          {categories.map((category) => (
            <details key={category.value} className="group border-b border-border" open>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-3 text-sm font-semibold text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                <span className="min-w-0 truncate">{category.label}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" />
              </summary>
              <ul className="m-0 list-none space-y-2.5 pb-3 pl-0">
                {category.specializations.map((specialization) => {
                  const id = `functional-area-${category.value}-${specialization.value}`;
                  return (
                    <li key={specialization.value}>
                      <label htmlFor={id} className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                        <input
                          id={id}
                          type="checkbox"
                          checked={selected.has(specialization.value)}
                          disabled={disabled}
                          onChange={() => onToggle(category.value, specialization.value)}
                          className="h-3.5 w-3.5 rounded border-input accent-primary focus-visible:ring-2 focus-visible:ring-ring"
                        />
                        <span className="min-w-0 flex-1 truncate">{specialization.label}</span>
                        <span className="text-count">{specialization.count}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </details>
          ))}
        </div>
      </div>
    </aside>
  );
}

export default function JobBoard({ initialJobs, initialLocations, initialFunctionalAreas, initialFilters, initialError = false }: Props) {
  const [filters, setFilters] = React.useState(initialFilters);
  const [jobs, setJobs] = React.useState(initialJobs);
  const [locations, setLocations] = React.useState(initialLocations);
  const [areas] = React.useState(initialFunctionalAreas);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(initialError ? "The Global Job Board is currently unavailable." : "");

  const load = React.useCallback(async (next: Filters) => {
    setLoading(true);
    setError("");
    try {
      const filterQuery = queryString(next);
      const [jobsResponse, locationResponse] = await Promise.all([
        fetch(`/api/jobs?${filterQuery}`),
        fetch(`/api/facets/locations?${filterQuery}`),
      ]);
      if (!jobsResponse.ok) throw new Error("unavailable");
      const [jobData, locationData] = await Promise.all([
        jobsResponse.json(),
        locationResponse.ok ? locationResponse.json() : [],
      ]);
      setJobs(jobData);
      setLocations(Array.isArray(locationData) ? locationData : []);
      setFilters(next);
      window.history.replaceState({}, "", `/jobs?${filterQuery}`);
    } catch {
      setError("The Global Job Board is currently unavailable.");
    } finally {
      setLoading(false);
    }
  }, []);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    void load({ ...filters, page: 1 });
  };
  const toggleFunctionalArea = (primary: string, secondary: string) => {
    const selection = functionalAreaSelection(filters, areas);
    if (selection.secondaryValues.has(secondary)) selection.secondaryValues.delete(secondary);
    else selection.secondaryValues.add(secondary);
    // Keep primary functional-area links backward compatible while using the
    // supported secondaryFunctionalArea API parameter for sidebar facets.
    void load({
      ...filters,
      functionalArea: [...selection.primaryValues].join(","),
      secondaryFunctionalArea: [...selection.secondaryValues].join(","),
      page: 1,
    });
  };
  const clearFilters = () => void load({ q: "", location: "", functionalArea: "", secondaryFunctionalArea: "", page: 1 });
  const page = Math.floor(jobs.offset / jobs.limit) + 1;
  const totalPages = Math.max(1, Math.ceil(jobs.estimatedTotalHits / jobs.limit));
  const hasFilters = Boolean(filters.q || filters.location || filters.functionalArea || filters.secondaryFunctionalArea);
  const activeFunctionalAreaSelection = functionalAreaSelection(filters, areas);

  return (
    <main className="container job-list" aria-busy={loading || undefined}>
      <form onSubmit={submit} className="job-search-wrapper" aria-label="Search jobs">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto]">
          <label className="sr-only" htmlFor="job-keywords">Keyword or job title</label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input id="job-keywords" value={filters.q} onChange={(event) => setFilters({ ...filters, q: event.target.value })} placeholder="Keyword or job title" className="h-10 rounded-sm bg-background py-2 pl-9 pr-3 focus-visible:ring-2" />
          </div>
          <label className="sr-only" htmlFor="job-location">Location</label>
          <Select value={filters.location || "__all_locations__"} onValueChange={(value) => setFilters({ ...filters, location: value === "__all_locations__" ? "" : value })}>
            <SelectTrigger id="job-location" className="h-10 rounded-sm bg-background focus:ring-2"><SelectValue placeholder="All locations" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all_locations__">All locations</SelectItem>
              {locations.map((item) => <SelectItem key={item.location} value={item.location}>{item.location} ({item.count})</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="submit" disabled={loading} className="h-10 rounded-sm">{loading ? "Searching…" : "Search jobs"}</Button>
        </div>
      </form>

      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="w-full shrink-0 lg:w-1/4">
          <FunctionalAreaFilters facets={areas} selected={activeFunctionalAreaSelection.secondaryValues} disabled={loading} onToggle={toggleFunctionalArea} />
        </div>
        <section className="min-w-0 flex-1" aria-label="Job results">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              {!error ? <p className="m-0 text-sm text-muted-foreground">{jobs.estimatedTotalHits} result{jobs.estimatedTotalHits === 1 ? "" : "s"}</p> : null}
            </div>
            {hasFilters ? <Button type="button" variant="ghost" size="sm" onClick={clearFilters} disabled={loading}>Clear filters</Button> : null}
          </div>

          {error ? <div className="rounded-sm border border-destructive/30 bg-card px-4 py-3 text-sm text-muted-foreground" role="alert">{error}</div> : null}
          {loading ? <p className="mb-3 text-sm text-muted-foreground" role="status">Loading jobs…</p> : null}
          {!loading && !error && jobs.hits.length === 0 ? <div className="rounded-sm border border-border bg-card px-5 py-8 text-center text-sm text-muted-foreground">No jobs match your search criteria.</div> : null}
          {!error && jobs.hits.map((job) => <JobResultCard key={job.id} job={job} />)}

          {!error && jobs.estimatedTotalHits > jobs.limit ? (
            <nav className="mt-6 flex items-center gap-3" aria-label="Job results pages">
              <Button type="button" variant="outline" size="sm" disabled={loading || page <= 1} onClick={() => void load({ ...filters, page: page - 1 })}>Previous</Button>
              <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
              <Button type="button" variant="outline" size="sm" disabled={loading || page >= totalPages} onClick={() => void load({ ...filters, page: page + 1 })}>Next</Button>
            </nav>
          ) : null}
        </section>
      </div>
    </main>
  );
}
